import React from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon, AcademicCapIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

const Awards: React.FC = () => {
  const certifications = [
    {
      title: "IPMA Project Management",
      date: "2016 · recertified 2021 & 2026",
      description: "International Project Management Association certificate, renewed through two recertifications",
      icon: <TrophyIcon className="h-12 w-12 text-yellow-600 mx-auto mb-4" />,
      bg: "bg-yellow-50",
      accent: "text-yellow-600"
    },
    {
      title: "ISTQB Certified Tester",
      date: "Foundation Level · 2022",
      description: "Software testing certification, applied hands-on when testing and accepting product releases",
      icon: <CheckBadgeIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />,
      bg: "bg-blue-50",
      accent: "text-blue-600"
    },
    {
      title: "Google Project Management",
      date: "Professional Certificate · 2024",
      description: "Google's project management program covering planning, execution and Agile delivery",
      icon: <AcademicCapIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />,
      bg: "bg-green-50",
      accent: "text-green-600"
    }
  ];

  const courses = [
    {
      title: "The AI Agents Bootcamp 2026",
      provider: "365 Careers · Udemy · 12 hours",
      description: "AI agent architecture, n8n, LangChain, RAG, LangGraph, LangSmith evaluation, and ReAct and ReWOO patterns for single and multi-agent systems",
      tags: ["AI Agents", "LangChain", "LangGraph", "RAG", "n8n"]
    },
    {
      title: "Master Product Management by Actually Building a Product",
      provider: "FutureVersity · Udemy · 17 hours",
      description: "Building a product end to end, from user research and market sizing to RICE prioritisation, no-code MVPs, OKRs and launch",
      tags: ["Product Discovery", "Prioritisation", "MVP", "OKRs"]
    },
    {
      title: "MBA in a Box: Business Lessons from a CEO",
      provider: "365 Careers · Udemy",
      description: "The five core MBA subjects: strategy, management and HR, marketing, financial statement analysis, and decision-making and negotiation",
      tags: ["Strategy", "Finance", "Marketing", "Negotiation"]
    },
    {
      title: "Cypress: Modern Automation Testing from Scratch",
      provider: "Rahul Shetty Academy · Udemy · 20.5 hours",
      description: "Fast, reliable UI, API and integration tests with Cypress, Mocha and Cucumber BDD frameworks, network mocking and CI integration",
      tags: ["Test Automation", "Cypress", "BDD", "CI/CD"]
    }
  ];

  return (
    <section id="awards" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Certifications
          </h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Professional certifications and courses in project management, product, AI and quality
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
        >
          {certifications.map((cert) => (
            <motion.div
              key={cert.title}
              whileHover={{ scale: 1.02 }}
              className={`${cert.bg} p-8 rounded-lg text-center shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              {cert.icon}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {cert.title}
              </h3>
              <p className={`${cert.accent} font-semibold text-lg mb-4`}>{cert.date}</p>
              <p className="text-gray-600">
                {cert.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Online Courses */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-16 max-w-5xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Online Courses
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {courses.map((course) => (
              <motion.div
                key={course.title}
                whileHover={{ scale: 1.02 }}
                className="bg-gray-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <h4 className="text-lg font-bold text-gray-900 mb-1">{course.title}</h4>
                <p className="text-primary-600 font-semibold text-sm mb-3">{course.provider}</p>
                <p className="text-gray-600 text-sm mb-4">{course.description}</p>
                <div className="flex flex-wrap gap-2">
                  {course.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-white text-gray-700 text-xs font-medium rounded-full border border-gray-200">
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Additional Achievement */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-8 rounded-lg max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Academic Excellence
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Master's Thesis</h4>
                <p className="text-primary-600 font-medium">Grade: 1.0 (Highest Distinction)</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Bachelor's Thesis</h4>
                <p className="text-blue-600 font-medium">Grade: 1.0 (Highest Distinction)</p>
              </div>
            </div>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default Awards;